import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  acceptWorkspaceAgreement,
  createWorkspaceAgreementTemplate,
  getWorkspaceAgreements,
  getWorkspaceWarnings,
} from "../../api/workspaceCompliance";
import WorkspaceComplianceFrame from "../../components/workspaceCompliance/WorkspaceComplianceFrame";
import {
  BooleanCard,
  fieldClass,
  InputLabel,
  SectionCard,
  textareaClass,
  WarningBanner,
} from "../../components/workspaceCompliance/WorkspaceComplianceUi";
import { useAuth } from "../../context/AuthContext";
import useOperatorWorkspace from "../../hooks/useOperatorWorkspace";
import {
  agreementKindOptions,
  approvalModeOptions,
  titleCaseComplianceLabel,
} from "../../constants/workspaceCompliance";
import { getAdvancedRecordTooltip } from "../../components/workspaceCompliance/advancedRecordsHelpContent";

const defaultTemplateForm = {
  title: "",
  agreement_kind: "lease",
  version: "",
  approval_mode: "agreement_required",
  agreement_status: "pending_acceptance",
  body_markdown: "",
  summary_text: "",
  is_active: true,
};

const defaultAcceptanceForm = {
  signature_name: "",
  note: "",
  acknowledge_role_ownership_payment_terms: false,
};

function formatDateTime(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return date.toLocaleString();
}

function AgreementSummaryCard({ label, value }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-base font-semibold text-slate-900">{value}</div>
    </article>
  );
}

function SnapshotCard({ snapshot }) {
  const snapshotJson =
    snapshot?.snapshot_json && typeof snapshot.snapshot_json === "object"
      ? snapshot.snapshot_json
      : {};
  return (
    <article className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-semibold text-slate-900">Snapshot #{snapshot.id}</div>
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          {formatDateTime(snapshot.created_at)}
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-4">
        <AgreementSummaryCard
          label="Occupancy"
          value={titleCaseComplianceLabel(snapshot.occupancy_mode)}
        />
        <AgreementSummaryCard
          label="Client ownership"
          value={titleCaseComplianceLabel(snapshot.client_ownership_mode)}
        />
        <AgreementSummaryCard
          label="Agreement version"
          value={snapshotJson.agreement_version || "Not captured"}
        />
        <AgreementSummaryCard
          label="Warning version"
          value={snapshotJson.warning_version || "Not captured"}
        />
      </div>
    </article>
  );
}

function AcceptanceCard({ acceptance }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-700">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-slate-900">
            {acceptance.signature_name || `User ${acceptance.user_id || "Unknown"}`}
          </div>
          <div className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
            {titleCaseComplianceLabel(acceptance.agreement_status)}
          </div>
        </div>
        <div className="text-sm text-slate-500">
          {formatDateTime(acceptance.accepted_at || acceptance.created_at)}
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <AgreementSummaryCard
          label="Version"
          value={acceptance.accepted_version || "Not recorded"}
        />
        <AgreementSummaryCard
          label="Workspace"
          value={`#${acceptance.canonical_workspace_id || acceptance.workspace_id || "?"}`}
        />
        <AgreementSummaryCard
          label="Rental"
          value={
            acceptance.rental_id ? `#${acceptance.rental_id}` : "Workspace-level acknowledgement"
          }
        />
      </div>
    </article>
  );
}

export default function OperatorWorkspaceAgreementsPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const { workspace, error: workspaceError } = useOperatorWorkspace(id);
  const [agreements, setAgreements] = useState({
    templates: [],
    acceptances: [],
    snapshots: [],
    warnings: [],
    warning_state_level: "none",
    warning_version: null,
  });
  const [warnings, setWarnings] = useState(null);
  const [templateForm, setTemplateForm] = useState(defaultTemplateForm);
  const [acceptanceForm, setAcceptanceForm] = useState(defaultAcceptanceForm);
  const [loading, setLoading] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingAcceptance, setSavingAcceptance] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  const loadPage = useCallback(async () => {
    if (!token || !id) return;
    const [agreementsResponse, warningsResponse] = await Promise.all([
      getWorkspaceAgreements(id, token),
      getWorkspaceWarnings(id, token),
    ]);
    setAgreements({
      templates: Array.isArray(agreementsResponse?.templates)
        ? agreementsResponse.templates
        : [],
      acceptances: Array.isArray(agreementsResponse?.acceptances)
        ? agreementsResponse.acceptances
        : [],
      snapshots: Array.isArray(agreementsResponse?.snapshots)
        ? agreementsResponse.snapshots
        : [],
      warnings: Array.isArray(agreementsResponse?.warnings)
        ? agreementsResponse.warnings
        : [],
      warning_state_level: agreementsResponse?.warning_state_level || "none",
      warning_version: agreementsResponse?.warning_version || null,
    });
    setWarnings(warningsResponse || null);
  }, [id, token]);

  useEffect(() => {
    if (!token || !id) return undefined;
    let active = true;
    setLoading(true);
    setError("");

    Promise.resolve()
      .then(loadPage)
      .catch((err) => {
        if (active) {
          setError(err.message || "Unable to load workspace agreements.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [id, loadPage, token]);

  const currentTemplate = useMemo(() => {
    const templates = Array.isArray(agreements.templates) ? agreements.templates : [];
    return templates[0] || null;
  }, [agreements.templates]);

  const currentAcceptance = useMemo(() => {
    if (!currentTemplate) return null;
    return (
      agreements.acceptances.find(
        (item) => String(item.agreement_template_id) === String(currentTemplate.id)
      ) || null
    );
  }, [agreements.acceptances, currentTemplate]);

  const handleTemplateSubmit = async (event) => {
    event.preventDefault();
    if (!token || !id) return;
    setSavingTemplate(true);
    setError("");
    setFlash("");
    try {
      await createWorkspaceAgreementTemplate(id, templateForm, token);
      setTemplateForm(defaultTemplateForm);
      await loadPage();
      setFlash("Agreement template saved.");
    } catch (err) {
      setError(err.message || "Unable to create workspace agreement template.");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleAcceptanceSubmit = async (event) => {
    event.preventDefault();
    if (!token || !id || !currentTemplate) return;
    setSavingAcceptance(true);
    setError("");
    setFlash("");
    try {
      await acceptWorkspaceAgreement(
        id,
        {
          template_id: currentTemplate.id,
          signature_name: acceptanceForm.signature_name || null,
          note: acceptanceForm.note || null,
          acknowledge_role_ownership_payment_terms: Boolean(
            acceptanceForm.acknowledge_role_ownership_payment_terms
          ),
        },
        token
      );
      setAcceptanceForm(defaultAcceptanceForm);
      await loadPage();
      setFlash("Agreement acknowledgement recorded.");
    } catch (err) {
      setError(err.message || "Unable to record workspace agreement acknowledgement.");
    } finally {
      setSavingAcceptance(false);
    }
  };

  return (
    <WorkspaceComplianceFrame
      workspace={workspace}
      workspaceId={id}
      title="Agreement Setup"
      description="Review the agreement text, versions, and renter acceptance records for this workspace."
    >
      {workspaceError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {workspaceError}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {flash ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {flash}
        </div>
      ) : null}

      <WarningBanner
        strictModeEnabled={Boolean(warnings?.strict_mode_enabled)}
        publishBlocked={Boolean(warnings?.publish_blocked)}
        agreementCompletionBlocked={Boolean(warnings?.agreement_completion_blocked)}
        warningStateLevel={warnings?.warning_state_level || agreements.warning_state_level}
        warnings={warnings?.warnings || agreements.warnings || []}
        disclaimer
      />

      <section className="grid gap-4 md:grid-cols-4">
        <AgreementSummaryCard
          label="Current version"
          value={currentTemplate?.version || "Not set"}
        />
        <AgreementSummaryCard
          label="Current status"
          value={currentAcceptance ? "Accepted" : "Not accepted"}
        />
        <AgreementSummaryCard
          label="Accepted by"
          value={currentAcceptance?.signature_name || currentAcceptance?.user_id || "Pending"}
        />
        <AgreementSummaryCard
          label="Accepted at"
          value={
            currentAcceptance?.accepted_at
              ? formatDateTime(currentAcceptance.accepted_at)
              : "Pending"
          }
        />
      </section>

      <div className="grid gap-6 xl:grid-cols-[0.95fr,1.05fr]">
        <SectionCard
          title="Current Agreement State"
          description="This is the agreement currently attached to this rental workspace."
          tooltip="Shows the agreement currently used for this workspace and whether it has been accepted."
        >
          {loading ? <p className="text-sm text-slate-600">Loading workspace agreements...</p> : null}
          {currentTemplate ? (
            <article className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">
                    {titleCaseComplianceLabel(currentTemplate.agreement_kind)}
                  </div>
                  <h2 className="mt-2 text-xl font-semibold text-slate-900">
                    {currentTemplate.title}
                  </h2>
                </div>
                <div className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-700">
                  Version {currentTemplate.version}
                </div>
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                {currentTemplate.summary_text || "No agreement summary has been provided yet."}
              </p>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <AgreementSummaryCard
                  label="Status"
                  value={titleCaseComplianceLabel(currentTemplate.agreement_status)}
                />
                <AgreementSummaryCard
                  label="Approval mode"
                  value={titleCaseComplianceLabel(currentTemplate.approval_mode)}
                />
                <AgreementSummaryCard
                  label="Active template"
                  value={currentTemplate.is_active ? "Yes" : "No"}
                />
              </div>
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm leading-6 text-slate-700 whitespace-pre-wrap">
                {currentTemplate.body_markdown}
              </div>
            </article>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-600">
              No agreement has been added for this workspace yet. Add one when the renter should review written terms before using the space.
            </div>
          )}

          {currentTemplate ? (
            <form
              className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5"
              onSubmit={handleAcceptanceSubmit}
            >
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Record renter acceptance</h3>
                <p className="mt-2 text-sm text-slate-600">
                  Use this when the renter has reviewed and accepted the current agreement version.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <InputLabel label="Signature name" tooltip={getAdvancedRecordTooltip("Signature name")}>
                  <input
                    className={fieldClass}
                    value={acceptanceForm.signature_name}
                    onChange={(event) =>
                      setAcceptanceForm((prev) => ({
                        ...prev,
                        signature_name: event.target.value,
                      }))
                    }
                  />
                </InputLabel>
                <InputLabel label="Note" tooltip={getAdvancedRecordTooltip("Note")}>
                  <input
                    className={fieldClass}
                    value={acceptanceForm.note}
                    onChange={(event) =>
                      setAcceptanceForm((prev) => ({
                        ...prev,
                        note: event.target.value,
                      }))
                    }
                  />
                </InputLabel>
              </div>
              <BooleanCard
                label="Renter accepted the role, ownership, and payment terms?"
                tooltip={getAdvancedRecordTooltip("Renter accepted the role, ownership, and payment terms?")}
                checked={Boolean(acceptanceForm.acknowledge_role_ownership_payment_terms)}
                onChange={(value) =>
                  setAcceptanceForm((prev) => ({
                    ...prev,
                    acknowledge_role_ownership_payment_terms: value,
                  }))
                }
              />
              <button
                type="submit"
                disabled={savingAcceptance}
                className="rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingAcceptance ? "Recording..." : "Record Acceptance"}
              </button>
            </form>
          ) : null}
        </SectionCard>

        <SectionCard
          title="Agreement Text"
          description="Add or update the agreement text for this rental workspace."
          tooltip={getAdvancedRecordTooltip("Agreement Setup")}
        >
          <form className="space-y-4" onSubmit={handleTemplateSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <InputLabel label="Title" tooltip={getAdvancedRecordTooltip("Title")}>
                <input
                  className={fieldClass}
                  value={templateForm.title}
                  onChange={(event) =>
                    setTemplateForm((prev) => ({
                      ...prev,
                      title: event.target.value,
                    }))
                  }
                  required
                />
              </InputLabel>
              <InputLabel label="Version" tooltip={getAdvancedRecordTooltip("Version")}>
                <input
                  className={fieldClass}
                  value={templateForm.version}
                  onChange={(event) =>
                    setTemplateForm((prev) => ({
                      ...prev,
                      version: event.target.value,
                    }))
                  }
                  required
                />
              </InputLabel>
              <InputLabel label="Agreement type" tooltip={getAdvancedRecordTooltip("Agreement type")}>
                <select
                  className={fieldClass}
                  value={templateForm.agreement_kind}
                  onChange={(event) =>
                    setTemplateForm((prev) => ({
                      ...prev,
                      agreement_kind: event.target.value,
                    }))
                  }
                >
                  {agreementKindOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </InputLabel>
              <InputLabel label="When is it required?" tooltip={getAdvancedRecordTooltip("When is it required?")}>
                <select
                  className={fieldClass}
                  value={templateForm.approval_mode}
                  onChange={(event) =>
                    setTemplateForm((prev) => ({
                      ...prev,
                      approval_mode: event.target.value,
                    }))
                  }
                >
                  {approvalModeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </InputLabel>
            </div>
            <InputLabel label="Summary" tooltip={getAdvancedRecordTooltip("Summary")}>
              <textarea
                className={textareaClass}
                value={templateForm.summary_text}
                onChange={(event) =>
                  setTemplateForm((prev) => ({
                    ...prev,
                    summary_text: event.target.value,
                  }))
                }
              />
            </InputLabel>
            <InputLabel label="Agreement terms" tooltip={getAdvancedRecordTooltip("Agreement terms")}>
              <textarea
                className={textareaClass}
                value={templateForm.body_markdown}
                onChange={(event) =>
                  setTemplateForm((prev) => ({
                    ...prev,
                    body_markdown: event.target.value,
                  }))
                }
                required
              />
            </InputLabel>
            <BooleanCard
              label="Use this agreement for this workspace?"
              tooltip={getAdvancedRecordTooltip("Use this agreement for this workspace?")}
              checked={Boolean(templateForm.is_active)}
              onChange={(value) =>
                setTemplateForm((prev) => ({
                  ...prev,
                  is_active: value,
                }))
              }
            />
            <button
              type="submit"
              disabled={savingTemplate}
              className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingTemplate ? "Saving..." : "Save Agreement"}
            </button>
          </form>

          <div className="space-y-3 border-t border-slate-200 pt-5">
            <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              Agreement versions
            </h3>
            {agreements.templates.map((template) => (
              <article
                key={template.id}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="font-semibold text-slate-900">
                    {template.title} - v{template.version}
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    {titleCaseComplianceLabel(template.agreement_status)}
                  </div>
                </div>
                <div className="mt-2 text-slate-600">
                  {template.summary_text || "No summary saved."}
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  Created {formatDateTime(template.created_at)}
                </div>
              </article>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard
        title="Acceptance Records"
        description="Use these records to confirm who accepted which agreement version and what rental setup was in place at that time."
        tooltip="Review historical agreement acceptances and saved setup snapshots for this workspace."
      >
        <div className="grid gap-6 xl:grid-cols-2">
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              Acceptance history
            </h3>
            {agreements.acceptances.map((acceptance) => (
              <AcceptanceCard key={acceptance.id} acceptance={acceptance} />
            ))}
            {agreements.acceptances.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-600">
                No renter acceptance has been recorded for this workspace yet.
              </div>
            ) : null}
          </div>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              Saved setup history
            </h3>
            {agreements.snapshots.map((snapshot) => (
              <SnapshotCard key={snapshot.id} snapshot={snapshot} />
            ))}
            {agreements.snapshots.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-600">
                No saved setup snapshots have been captured yet.
              </div>
            ) : null}
          </div>
        </div>
      </SectionCard>
    </WorkspaceComplianceFrame>
  );
}
